import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { Loading } from "@/components/atoms/Loading"
import { analytics } from "@/lib/analytics"

function extractLabel(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(extractLabel).join(' ').trim();
  if (React.isValidElement(children)) return extractLabel((children.props as { children?: React.ReactNode }).children);
  return '';
}

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 relative cursor-pointer",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-transparent hover:bg-hover hover:text-accent-foreground",
        outlineLight:
          "border border-transparent bg-background-light hover:bg-background-light/100 hover:text-accent-foreground hover:border-input",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        tertiary:
          "bg-tertiary text-tertiary-foreground hover:bg-tertiary/80 disabled:opacity-100 disabled:text-card-foreground disabled:bg-border disabled:cursor-not-allowed",
        ghost: "hover:bg-accent hover:text-accent-foreground hover:bg-[#F7F3F2]",
        link: "text-link underline underline-offset-4 hover:underline",
        refute:
          "border border-refute-foreground/20 bg-refute text-refute-foreground hover:border-refute-foreground/50 hover:text-refute-foreground",
        noora: "bg-card-reverse text-card-reverse-foreground hover:bg-card-reverse/80 font-bold",
        basic: ""
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-7 rounded-sm px-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        basic: ""
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean,
  loading?: boolean,
  textContainerClassName?: string,
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, textContainerClassName, children, disabled, onClick, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      const label = extractLabel(children);
      analytics.track('Button Clicked', { label: label || '(unlabeled)', variant: variant ?? 'default' });
      onClick?.(e);
    };

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        onClick={handleClick}
        children={
          <React.Fragment>
            <div className={cn("flex items-center gap-2", { "invisible": loading }, textContainerClassName)}>
              {children}
            </div>
            {
              loading && (
                <Loading
                  className="absolute inset-0 flex items-center justify-center z-10"
                  iconClassName={cn("text-inherit")}
                />
              )
            }
          </React.Fragment>
        }
        disabled={disabled || loading}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
